import {
    useLayoutEffect,
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    TouchableOpacity,
    Platform,
    Image,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitToast,
    NCoreUIKitTheme,
    TextAreaInput,
    PageContainer,
    TextInput,
    SelectBox,
    Button,
    Dialog,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute,
    type RouteProp
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type {
    EditCompanyRequestRequestType
} from "../../../../../services/restAPI/actions/editCompanyRequest/types";
import type {
    CompanyUpdateRequestsObjectType
} from "../../../../../services/restAPI/actions/getCompanyUpdateRequests/types";
import type {
    GetMyCompanyDetailPayloadType
} from "../../../../../services/restAPI/actions/getMyCompanyDetail/types";
import type {
    SectorsObjectType
} from "../../../../../services/restAPI/actions/getSectors/types";
import type AppStackParamList from "../../../../../types/navigation/app";
import type {
    SelectedFileType
} from "../../../editProfile/type";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    AlertCircle,
    Check,
    ChevronLeft,
    CircleAlert,
    CircleQuestionMark,
    Pencil
} from "lucide-react-native";
import ImageCropPicker from "react-native-image-crop-picker";
import validator from "validator";
import gmkonyalogo from "../../../../../assets/images/gmkonyalogo.png";
import {
    RESTService
} from "../../../../../services/restAPI";
import {
    handleUpload
} from "../../../../../utils";
import {
    IS_EXPORTS
} from "../constant";

const CompanyEdit = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const route = useRoute<RouteProp<AppStackParamList, "CompanyEdit">>();

    const {
        companyID
    } = route.params;

    const {
        spaces,
        borders,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const inset = useSafeAreaInsets();

    const [initialData,
        setInitialData] = useState<GetMyCompanyDetailPayloadType>();
    const [data,
        setData] = useState<GetMyCompanyDetailPayloadType>();
    const [isCompanyUpdateRequest,
        setIsCompanyUpdateRequest] = useState<CompanyUpdateRequestsObjectType>();
    const [sectorsData,
        setSectorsData]= useState<Array<SectorsObjectType>>([]);
    const [updateParams,
        setUpdateParams] = useState<EditCompanyRequestRequestType>({
            companyID: companyID
        });
    const [successDialog,
        setSuccessDialog] = useState(false);
    const [verificationDialog,
        setVerificationDialog] = useState(false);
    const [loading,
        setLoading] = useState(true);
    const [selectedImage,
        setSelectedImage] = useState<string | null>(null);
    const [selctedFile,
        setSelectedFile] = useState<SelectedFileType | null>(null);

    useEffect(() => {
        getUpdateRequests();
        getCompanyData();
        getSectors();
    }, []);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={[
                    stylesheet.headerContainer,
                    {
                        paddingTop: inset.top,
                        paddingHorizontal: spaces.spacingMd,
                        backgroundColor: colors.content.container.default
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.backButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
                <View
                    style={[
                        stylesheet.headerTitleContainer,
                        {
                            top: inset.top + spaces.spacingSm
                        }
                    ]}
                >
                    <Text
                        variant="titleLargeSize"
                        color="high"
                        style={stylesheet.headerTitle}
                    >
                        {localize("updateCompanyInformation")}
                    </Text>
                </View>
            </View>
        });
    }, [navigation]);

    const getSectors = () => {
        RESTService.action("GetSectors", null)
            .then((res) => {

                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setSectorsData(res.payload);
                }

                setLoading(false);
            }).catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const getCompanyData = () => {
        RESTService.action("GetMyCompanyDetail", {
            companyID: companyID
        })
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    setData(res.payload);
                    setInitialData(res.payload);
                }
                setLoading(false);
            }).catch((err) => {
                console.log(err);
                setLoading(false);
            });
    };

    const getUpdateRequests = () => {
        RESTService.action("GetCompanyUpdateRequests", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    const updateReq: Array<CompanyUpdateRequestsObjectType> = res.payload;
                    const _updateReq = updateReq.find(e => e.companyID === companyID);

                    setIsCompanyUpdateRequest(_updateReq);

                    if(_updateReq) {
                        NCoreUIKitSnackBar.open({
                            title: localize("pendingRequestExists"),
                            type: "danger",
                            isShowAction: false,
                            autoCloseDelay: 10000,
                            icon: ({
                                color
                            }) => <CircleAlert
                                color={colors.content.icon[color ? color : "default"]}
                            />
                        });
                    }
                }
            }).catch((err) => {
                console.log(err);
            });
    };

    const sendCompanyEditReq = () => {
        if(!initialData) {
            return;
        }

        if(!updateParams) {
            if(selctedFile) {
                handleUpdateProfilePhoto();
                return;
            }
            NCoreUIKitToast.open({
                title: localize("emptyRequestError"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }
        const params:EditCompanyRequestRequestType = {
            companyID: companyID
        };

        if(updateParams.aboutUs && updateParams.aboutUs.length && updateParams.aboutUs.trim() !== initialData.aboutUs.trim()) {
            params.aboutUs = updateParams.aboutUs;
        }

        if(updateParams.title && updateParams.title.length && updateParams.title.trim() !== initialData.title.trim()) {
            params.title = updateParams.title;
        }

        if(updateParams.mail && updateParams.mail.length && updateParams.mail.trim() !== initialData.mail.trim() && validator.isEmail(updateParams.mail)) {
            params.mail = updateParams.mail;
        }

        if(updateParams.address && updateParams.address.length && updateParams.address.trim() !== initialData.address.trim()) {
            params.address = updateParams.address;
        }

        if(updateParams.phoneNumber && updateParams.phoneNumber.length && updateParams.phoneNumber.trim() !== initialData.phoneNumber.trim() && validator.isMobilePhone(updateParams.phoneNumber)) {
            params.phoneNumber = updateParams.phoneNumber;
        }

        if(updateParams.sectorIDs && updateParams.sectorIDs.length) {
            params.sectorIDs = updateParams.sectorIDs;
        }

        if(updateParams.taxOffice && updateParams.taxOffice.length && updateParams.taxOffice.trim() !== initialData.taxOffice.trim()) {
            params.taxOffice = updateParams.taxOffice;
        }

        if(updateParams.taxNumber && updateParams.taxNumber.length && updateParams.taxNumber.trim() !== initialData.taxNumber.trim()) {
            params.taxNumber = updateParams.taxNumber;
        }

        if(updateParams.foundationYear && updateParams.foundationYear !== initialData.foundationYear) {
            params.foundationYear = updateParams.foundationYear;
        }

        if("isExports" in updateParams) {
            params.isExports = updateParams.isExports;
        }

        if(updateParams.website && updateParams.website.length && updateParams.website.trim() !== initialData.website.trim()) {
            params.website = updateParams.website;
        }

        if (Object.keys(params).length === 1) {
            if(selctedFile) {
                handleUpdateProfilePhoto();
                return;
            }
            NCoreUIKitToast.open({
                title: localize("emptyRequestError"),
                isShowAction: false,
                type: "warning",
                icon: ({
                    color
                }) => <AlertCircle
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        };

        RESTService.action("EditCompanyRequest", params)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    if(selctedFile) {
                        handleUpdateProfilePhoto();
                        return;
                    }
                    setSuccessDialog(true);
                }
            }).catch((err) => {
                console.log(err);
            });
    };

    const handleUpdateProfilePhoto = () => {
        if(!selctedFile) {
            return;
        }

        if(!(initialData && initialData.companyID)) {
            return;
        }

        RESTService.action("UpdateCompanyPhoto", {
            companyID: initialData.companyID
        })
            .then(async (res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {

                    await handleUpload(selctedFile, res.payload.uploadToken)
                        .then(() => {
                            setSuccessDialog(true);
                        })
                        .catch((err) => {
                            console.log("err", err);
                            throw err;
                        });
                }
            }).catch((err) => {
                console.log("err", err);
            });
    };

    const renderProfilePhoto = () => {
        return <View
            style={[
                stylesheet.profilePhotoWrapper,
                {
                    borderRadius: radiuses.full,
                    marginTop: spaces.spacingLg,
                    marginBottom: spaces.spacingXl
                }
            ]}
        >
            <Image
                source={selectedImage || initialData?.logoURL ? {
                    uri: selectedImage ? selectedImage : initialData?.logoURL
                } : gmkonyalogo}
                resizeMode="cover"
                style={[
                    stylesheet.profilePhotoImage,
                    {
                        borderRadius: radiuses.md
                    }
                ]}
            />
            <TouchableOpacity
                style={[
                    stylesheet.photoEditButton,
                    {
                        right: -spaces.spacingSm,
                        bottom: -spaces.spacingSm,
                        backgroundColor: colors.content.container.mid,
                        borderRadius: radiuses.full,
                        padding: spaces.spacingSm,
                        borderWidth: borders.subtract,
                        borderColor: colors.content.border.subtle
                    }
                ]}
                onPress={() => {
                    ImageCropPicker.openPicker({
                        cropping: true,
                        multiple: false
                    }).then(image => {
                        const format = image.mime?.split("/")[1];

                        if (image.size > 20 * 1024 * 1024) {
                            NCoreUIKitToast.open({
                                title: localize("imageTooLarge"),
                                type: "warning"
                            });
                            return;
                        }

                        if (![
                            "jpg",
                            "jpeg",
                            "png",
                            "heic"
                        ].includes(format as string)) {
                            NCoreUIKitToast.open({
                                title: localize("invalidFormat"),
                                type: "warning"
                            });
                            return;
                        }

                        const selectedFile = {
                            name: image.path.split("/").pop(),
                            type: image.mime,
                            uri: Platform.OS === "ios" ? image.path.startsWith("file://") ? image.path : `file:/${image.path}` : image.path
                        } as unknown as { name: string; type: string; uri: string; };
                        setSelectedImage(image.path);
                        setSelectedFile(selectedFile);
                    }).catch((err) => {
                        console.error(err);
                    });
                }}
            >
                <Pencil
                    size={20}
                />
            </TouchableOpacity>
        </View>;
    };

    const renderMyCompanyData = () => {
        if(data) {
            const sectorInitialSelected: Array<{
                __title: string;
                __key: string;
            }> = data.sectors.map(e => {
                return {
                    __key: e.sectorID,
                    __title: e.sectorName
                };
            });

            return <View
                style={{
                    marginBottom: spaces.spacingLg
                }}
            >
                <TextInput
                    title={localize("companyOrOrganization")}
                    type={updateParams?.title !== undefined && updateParams.title.length <= 2 ? "danger" : "default"}
                    hintText={updateParams?.title !== undefined && updateParams.title.length <= 2 ? (localize("invalidTitle")) : undefined}
                    spreadBehaviour="stretch"
                    isDisabled={isCompanyUpdateRequest ? true : false}
                    placeholder={data?.title}
                    defaultValue={data?.title}
                    onChangeText={(e) => {
                        setData({
                            ...data,
                            title: e
                        });
                        setUpdateParams({
                            ...updateParams,
                            title: e
                        });
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <TextInput
                    title={localize("foundationYear")}
                    type={updateParams?.foundationYear !== undefined && (updateParams.foundationYear <= 1923 || updateParams.foundationYear >= 2026) ? "danger" : "default"}
                    hintText={updateParams?.foundationYear !== undefined && (updateParams.foundationYear <= 1923 || updateParams.foundationYear >= 2026) ? (localize("invalidFoundationYear")) : undefined}
                    spreadBehaviour="stretch"
                    placeholder={String(data?.foundationYear)}
                    defaultValue={String(data?.foundationYear)}
                    isDisabled={isCompanyUpdateRequest ? true : false}
                    onChangeText={(e) => {
                        setData({
                            ...data,
                            foundationYear: Number(e)
                        });
                        setUpdateParams({
                            ...updateParams,
                            foundationYear: Number(e)
                        });
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <SelectBox
                    initialSelectedItems={sectorInitialSelected}
                    isDisabled={isCompanyUpdateRequest ? true : false}
                    keyExtractor={(item) => item._id }
                    titleExtractor={(item) => item.value}
                    spreadBehaviour="stretch"
                    data={sectorsData}
                    title={localize("sector")}
                    isWorkWithRealtime={false}
                    isCleanEnabled={false}
                    minChoice={1}
                    isMultipleSelect={true}
                    selectSheetProps={{
                        style:{
                            borderTopRightRadius: radiuses.sm,
                            borderTopLeftRadius: radiuses.sm
                        }
                    }}
                    onOk={(
                        selectedItems
                    ) => {
                        if(selectedItems && selectedItems.length) {
                            const sectorIDs = selectedItems.map(e => e.__key);
                            const _sectors = selectedItems.map(e => {
                                return {
                                    sectorID: e.__key,
                                    sectorName: e.__title
                                };
                            });
                            setData({
                                ...data,
                                sectors: _sectors
                            });
                            setUpdateParams({
                                ...updateParams,
                                sectorIDs: sectorIDs
                            });
                        }
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <TextInput
                    title={localize("companyEmailAddress")}
                    type={updateParams?.mail !== undefined && !validator.isEmail(updateParams.mail) ? "danger" : "default"}
                    hintText={updateParams?.mail !== undefined && !validator.isEmail(updateParams.mail) ? (localize("invalidEmail")) : undefined}
                    spreadBehaviour="stretch"
                    isDisabled={isCompanyUpdateRequest ? true : false}
                    placeholder={data?.mail}
                    defaultValue={data?.mail}
                    onChangeText={(e) => {
                        setData({
                            ...data,
                            mail: e
                        });
                        setUpdateParams({
                            ...updateParams,
                            mail: e
                        });
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <TextAreaInput
                    title={localize("aboutCompany")}
                    type={updateParams?.aboutUs !== undefined && updateParams.aboutUs.length <= 3 ? "danger" : "default"}
                    hintText={updateParams?.aboutUs !== undefined && updateParams.aboutUs.length <= 3 ? (localize("aboutCannotBeEmpty")) : undefined}
                    spreadBehaviour="stretch"
                    isDisabled={isCompanyUpdateRequest ? true : false}
                    placeholder={data?.aboutUs}
                    defaultValue={data?.aboutUs}
                    onChangeText={(e) => {
                        setData({
                            ...data,
                            aboutUs: e
                        });
                        setUpdateParams({
                            ...updateParams,
                            aboutUs: e
                        });
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <TextInput
                    title={localize("address")}
                    type={updateParams?.address !== undefined && updateParams.address.length <= 2 ? "danger" : "default"}
                    hintText={updateParams?.address !== undefined && updateParams.address.length <= 2 ? (localize("invalidAddress")) : undefined}
                    spreadBehaviour="stretch"
                    isDisabled={isCompanyUpdateRequest ? true : false}
                    placeholder={data?.address}
                    defaultValue={data?.address}
                    onChangeText={(e) => {
                        setData({
                            ...data,
                            address: e
                        });
                        setUpdateParams({
                            ...updateParams,
                            address: e
                        });
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <TextInput
                    title={localize("companyPhone")}
                    type={updateParams?.phoneNumber !== undefined && !validator.isMobilePhone(updateParams.phoneNumber) ? "danger" : "default"}
                    hintText={updateParams?.phoneNumber !== undefined && !validator.isMobilePhone(updateParams.phoneNumber) ? (localize("invalidPhoneNumber")) : undefined}
                    spreadBehaviour="stretch"
                    placeholder={data?.phoneNumber}
                    isDisabled={isCompanyUpdateRequest ? true : false}
                    defaultValue={data?.phoneNumber}
                    onChangeText={(e) => {
                        setData({
                            ...data,
                            phoneNumber: e
                        });
                        setUpdateParams({
                            ...updateParams,
                            phoneNumber: e
                        });
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <TextInput
                    title={localize("taxOffice")}
                    type={updateParams?.taxOffice !== undefined && updateParams.taxOffice.length <= 2 ? "danger" : "default"}
                    hintText={updateParams?.taxOffice !== undefined && updateParams.taxOffice.length <= 2 ? (localize("invalidTaxOffice")) : undefined}
                    spreadBehaviour="stretch"
                    isDisabled={isCompanyUpdateRequest ? true : false}
                    placeholder={data?.taxOffice}
                    defaultValue={data?.taxOffice}
                    onChangeText={(e) => {
                        setData({
                            ...data,
                            taxOffice: e
                        });
                        setUpdateParams({
                            ...updateParams,
                            taxOffice: e
                        });
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <TextInput
                    title={localize("taxNumber")}
                    type={updateParams?.taxNumber !== undefined && updateParams.taxNumber.length <= 9 ? "danger" : "default"}
                    hintText={updateParams?.taxNumber !== undefined && updateParams.taxNumber.length <= 9 ? (localize("invalidTaxNumber")) : undefined}
                    spreadBehaviour="stretch"
                    isDisabled={isCompanyUpdateRequest ? true : false}
                    placeholder={data?.taxNumber}
                    defaultValue={data?.taxNumber}
                    onChangeText={(e) => {
                        setData({
                            ...data,
                            taxNumber: e
                        });
                        setUpdateParams({
                            ...updateParams,
                            taxNumber: e
                        });
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <SelectBox
                    initialSelectedItems={data.isCompanyExports ? [
                        {
                            __key: "true",
                            __title: "Evet"
                        }
                    ] : [
                        {
                            __key: "false",
                            __title: "Hayır"
                        }
                    ]}
                    isDisabled={isCompanyUpdateRequest ? true : false}
                    keyExtractor={(item) => item.key }
                    titleExtractor={(item) => item.value}
                    spreadBehaviour="stretch"
                    data={IS_EXPORTS}
                    title={localize("doesExport")}
                    isWorkWithRealtime={false}
                    minChoice={1}
                    maxChoice={1}
                    isMultipleSelect={false}
                    selectSheetProps={{
                        style:{
                            borderTopRightRadius: radiuses.sm,
                            borderTopLeftRadius: radiuses.sm
                        }
                    }}
                    onOk={(
                        selectedItems
                    ) => {
                        if(selectedItems && selectedItems.length) {
                            const _isExports = selectedItems[0]?.__key === "true" ? true : false;

                            setUpdateParams({
                                ...updateParams,
                                isExports: _isExports
                            });
                        }
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <TextInput
                    title={localize("website")}
                    type={!data?.website || !validator.isURL(data.website, {
                        validate_length: true
                    }) ? "danger" : "default"}
                    hintText={!data?.website || !validator.isURL(data.website, {
                        validate_length: true
                    }) ? (localize("invalidWebsite")) : undefined}
                    isDisabled={isCompanyUpdateRequest ? true : false}
                    spreadBehaviour="stretch"
                    placeholder={data?.website}
                    defaultValue={data?.website}
                    onChangeText={(e) => {
                        setData({
                            ...data,
                            website: e
                        });
                        setUpdateParams({
                            ...updateParams,
                            website: e
                        });
                    }}
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
            </View>;
        } else {
            return null;
        }
    };

    const renderSuccessDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            backgroundColor: colors.system.low.success,
                            marginBottom: spaces.spacingLg,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingMd
                        }
                    ]}
                >
                    <Check
                        color={colors.project.brandPrimary}
                        size={30}
                    />
                </View>
                <Text
                    style={stylesheet.dialogTitle}
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("submittedSuccessfully")}
                </Text>
            </View>}
            primaryButtonProps={{
                customBorderColor: colors.project.brandPrimary,
                customTextColor: colors.project.brandWhite,
                customColor: colors.project.brandPrimary,
                title: localize("backToHome"),
                onPress: () => {
                    setSuccessDialog(false);
                    navigation.goBack();
                }
            }}
            content={localize("changesUnderReview")}
            onOverlayPress={() => {
                setSuccessDialog(false);
                navigation.navigate("Profile");
            }}
            isVisible={successDialog}
            variant="ok"
        />;
    };

    const renderVerificationDialog = () => {
        return <Dialog
            secondaryButtonProps={{
                customBorderColor: colors.project.black,
                customTextColor: colors.project.black,
                customColor: colors.project.brandWhite,
                title: localize("abandon"),
                onPress: () => {
                    setVerificationDialog(false);
                }
            }}
            headerComponent={<View
                style={stylesheet.dialogHeaderContainer}
            >
                <View
                    style={[
                        stylesheet.dialogIconWrapper,
                        {
                            marginBottom: spaces.spacingLg,
                            borderRadius: radiuses.full,
                            padding: spaces.spacingMd
                        }
                    ]}
                >
                    <CircleQuestionMark
                        color={colors.project.gold}
                        size={50}
                    />
                </View>
                <Text
                    style={stylesheet.dialogTitle}
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("confirmSubmitForApproval")}
                </Text>
            </View>}
            primaryButtonProps={{
                title: localize("submitForApproval"),
                customBorderColor: colors.project.gold,
                customTextColor: colors.project.brandWhite,
                customColor: colors.project.gold,
                onPress: () => {
                    setVerificationDialog(false);
                    sendCompanyEditReq();
                }
            }}
            content={localize("submitForApprovalDescription")}
            onOverlayPress={() => {
                setVerificationDialog(false);
            }}
            isVisible={verificationDialog}
            variant="yes-no"
        />;
    };

    const renderActionButtons = () => {
        if(isCompanyUpdateRequest && data) {
            return <View
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                <Button
                    size="large"
                    title={localize("reviewEditRequest")}
                    customColor="gold"
                    customBorderColor="gold"
                    customTextColor="black"
                    spreadBehaviour="stretch"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                    onPress={() => {
                        navigation.navigate("CompanyEditView", {
                            updateRequest: isCompanyUpdateRequest
                        });
                    }}
                />
            </View>;
        }

        if(data && !isCompanyUpdateRequest) {
            return <View
                style={{
                    marginBottom: spaces.spacingMd
                }}
            >
                <Button
                    size="large"
                    title={localize("submitEditRequestForApproval")}
                    customColor="black"
                    customBorderColor="black"
                    customTextColor={colors.project.brandWhite}
                    onPress={() => {
                        setVerificationDialog(true);
                    }}
                    isDisabled={(Object.keys(updateParams).length === 1 && !selctedFile)}
                    spreadBehaviour="stretch"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
                <Button
                    size="large"
                    title={localize("abandon")}
                    onPress={() => {
                        navigation.goBack();
                    }}
                    spreadBehaviour="stretch"
                    variant="outline"
                    customBorderColor="black"
                    style={{
                        marginBottom: spaces.spacingMd
                    }}
                />
            </View>;
        }

        if(!data && isCompanyUpdateRequest) {
            return null;
        }
        return null;

    };

    if(loading) {
        return <PageContainer>
            <ActivityIndicator
                size={"large"}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <PageContainer
        backgroundColor="default"
        isScrollable={true}
        scrollViewProps={{
            showsVerticalScrollIndicator: false
        }}
    >
        {renderProfilePhoto()}
        {renderMyCompanyData()}
        {renderActionButtons()}
        {renderSuccessDialog()}
        {renderVerificationDialog()}
    </PageContainer>;
};
export default CompanyEdit;
